import { Box, Button, CircularProgress, Dialog, FormControl, IconButton, TextField, Typography, useTheme } from '@mui/material'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import * as Yup from 'yup'
import { Icons } from '../../utils/utilities'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { toast } from 'react-toastify'
import { useMeetingStore } from '../../store/meeting.store'
import api from '../../service/api'

const EditMeetingDetails = ( { open, handleClose, meeting } ) => {

    const [updating, setUpdating] = useState( false )

    const { palette, border } = useTheme()

    const MeetingStore = useMeetingStore()

    const initialValues = {
        title: meeting.title,
        starts_at: dayjs( meeting.starts_at ),
        ends_at: dayjs( meeting.ends_at ),
    }

    const validationSchema = Yup.object().shape( {
        title: Yup.string().required( "Title is a required field." ),
        starts_at: Yup.mixed().required( "Please specify start time." ),
        ends_at: Yup.mixed().test( "VALID_END_TIME", "Please enter a valid end time", ( val, { parent } ) => {
            if ( val <= parent.starts_at )
                return false
            const diff = ( new Date( val ).getTime() - new Date( parent.starts_at ).getTime() ) / ( 1000 * 60 * 60 )
            if ( diff < 0 )
                return false
            if ( diff > 10 )
                return false
            return true
        } )

    } )

    const updateMeeting = async ( values ) => {
        try {
            setUpdating( true )
            const { data: { success, message } } = await api.onlineMeetings.updateMeetingDetails( meeting.id, { updates: values, update_type: "details" } )
            if ( success ) {
                setUpdating( false )
                handleClose()
            }
            return toast( message )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while updating your meeting!" )
        } finally {
            setUpdating( false )
        }
    }

    return (
        <Dialog open={Boolean( open )} slotProps={{
            paper: {
                sx: {
                    width: "90%",
                    maxWidth: "md"
                }
            }
        }}>
            <Box>
                <Box padding="10px 20px" borderBottom={border[1]} display="flex" gap="20px" alignItems="center" justifyContent="space-between">
                    <Typography variant='h6'>Edit Meeting Details</Typography>
                    <IconButton onClick={handleClose}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
                <Box padding="20px">
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={updateMeeting} >
                        {( { values, setFieldValue } ) => {
                            return (
                                <Form >
                                    <FormControl fullWidth >
                                        <label htmlFor="title"><Typography variant='subtitle2'>Meeting title *</Typography></label>
                                        <Field as={TextField} name="title" placeholder='Title' id="title" size='small' />
                                        <Typography fontSize="12px" color="error"  ><ErrorMessage name='title' /></Typography>
                                    </FormControl>
                                    <FormControl fullWidth margin='normal' >
                                        <label htmlFor='starts_at' > <Typography gutterBottom variant="subtitle2" display="block" color={palette.labelColor}>Start this meeting at? *</Typography> </label>
                                        <Box>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    slotProps={{
                                                        textField: {
                                                            id: "starts_at",
                                                            size: "small",
                                                            color: "secondary"
                                                        }
                                                    }}
                                                    // format='DD-MM-YYYY hh:mm A'
                                                    value={dayjs( values.starts_at )}
                                                    onChange={( e ) => {
                                                        if ( e.isValid() )
                                                            setFieldValue( 'starts_at', e )
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                        <Typography fontSize="12px" color="error"  ><ErrorMessage name='starts_at' /></Typography>
                                    </FormControl>
                                    <FormControl fullWidth margin='normal' >
                                        <label htmlFor='ends_at' > <Typography gutterBottom variant="subtitle2" display="block" color={palette.labelColor}>End this meeting at? *</Typography> </label>
                                        <Box>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    slotProps={{
                                                        textField: {
                                                            id: "ends_at",
                                                            size: "small",
                                                            color: "secondary"
                                                        }
                                                    }}
                                                    // format='DD-MM-YYYY hh:mm A'
                                                    value={dayjs( values.ends_at )}
                                                    onChange={( e ) => {
                                                        if ( e.isValid() )
                                                            setFieldValue( 'ends_at', e )
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                        <Typography fontSize="12px" color="error"  ><ErrorMessage name='ends_at' /></Typography>
                                    </FormControl>
                                    <Box display="flex" alignItems="center" gap="20px" marginTop="20px">
                                        <Button disabled={updating} variant='contained' type="submit" startIcon={updating ? <CircularProgress size={20} /> : Icons.default.CheckIcon} disableElevation>Save Changes</Button>
                                        <Button disabled={updating} color="error" variant='contained' startIcon={Icons.default.CloseIcon} disableElevation>Cancel</Button>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                </Box>
            </Box>
        </Dialog>
    )
}

export default EditMeetingDetails
